/**
 * JSON Patch v2-Compatible Wrapper (powered by v3)
 *
 * This module wraps fast-json-patch v3 to provide v2-compatible behavior.
 * Uses v3 under the hood but transforms return values to match v2 expectations.
 *
 * Purpose:
 * - Allow gradual migration from v2 to v3
 * - Maintain backward compatibility with existing SST code
 * - Provide v3's strict validation with v2's return format
 * - SST Custom: Auto-create paths (not in RFC 6902 or v3)
 *
 * SST-Specific Features:
 * - ✅ Dot-to-slash conversion: Auto-converts '/path.to.prop' → '/path/to/prop'
 * - ✅ Path auto-creation: Creates intermediate objects/arrays for non-existent paths
 * - ✅ Prototype pollution protection: From v3
 * - ✅ Array return format: Like v2
 *
 * This makes rfc6902v2 a drop-in replacement for rfc6902 (original v2).
 *
 * @see docs/ARCHITECTURE.md for details
 */

const v3 = require('./jsonPatchRfc6902v3');

/**
 * SST Custom: Ensure a path exists by creating intermediate objects/arrays
 * This mimics the v2 behavior where paths are auto-created
 *
 * @param {Object} document - The document to ensure path in
 * @param {string} path - JSON Pointer path (e.g., '/a/b/c')
 * @returns {void}
 */
function ensurePath(document, path) {
    if (!path || path === '') return; // Root path

    const parts = path.split('/').filter(p => p !== '');
    if (parts.length === 0) return;

    let current = document;

    // Navigate through all parts except the last one
    for (let i = 0; i < parts.length - 1; i++) {
        const part = unescapePathComponent(parts[i]);
        const nextPart = parts[i + 1];

        if (current[part] === undefined || current[part] === null) {
            // Determine if next level should be array or object
            // Array if next part is numeric or '-'
            const nextIsArrayIndex = nextPart === '-' || /^\d+$/.test(nextPart);
            current[part] = nextIsArrayIndex ? [] : {};
        }

        current = current[part];
    }
}

/**
 * Unescape a path component (RFC 6901)
 * ~1 -> /
 * ~0 -> ~
 */
function unescapePathComponent(str) {
    return str.replace(/~1/g, '/').replace(/~0/g, '~');
}

/**
 * Apply a JSON Patch sequence to a document (v2-compatible)
 *
 * Returns an array of results (v2 format) instead of v3's object format.
 * Each element is the result/return value of the corresponding operation.
 *
 * @param {Object} document - The document to patch (will be mutated)
 * @param {Array} patches - Array of JSON Patch operations
 * @param {boolean} validate - Whether to validate operations (default: false for v2 compat)
 * @returns {Array} Array of operation results (v2 format)
 *
 * @example
 * const doc = { foo: 'bar' };
 * const results = apply(doc, [
 *   { op: 'replace', path: '/foo', value: 'baz' },
 *   { op: 'add', path: '/new', value: 'value' }
 * ]);
 * // results = ['bar', undefined]  // v2 format: [oldValue, ...]
 */
function apply(document, patches, validate = false) {
    try {
        if (!Array.isArray(patches) || patches.length === 0) {
            return [];
        }

        // SST Custom: Normalize and prepare patches
        // 1. Ensure paths start with '/' (RFC 6901 JSON Pointer requirement)
        // 2. Convert dot notation to slash notation (e.g., '/meta.oauth' → '/meta/oauth')
        // 3. Auto-create paths for non-existent intermediate objects/arrays
        patches.forEach(patch => {
            if (patch && typeof patch === 'object') {
                // Ensure leading '/' on path (RFC 6901 requires it; SST patches often omit it)
                if (patch.path && !patch.path.startsWith('/')) {
                    patch.path = '/' + patch.path;
                }
                if (patch.from && !patch.from.startsWith('/')) {
                    patch.from = '/' + patch.from;
                }
                // Convert dots to slashes in path
                if (patch.path && patch.path.includes('.')) {
                    patch.path = patch.path.replace(/\./g, '/');
                }
                // Convert dots to slashes in from (for copy/move)
                if (patch.from && patch.from.includes('.')) {
                    patch.from = patch.from.replace(/\./g, '/');
                }

                // Auto-create intermediate paths
                if (patch.path) {
                    ensurePath(document, patch.path);
                }
                if (patch.from && (patch.op === 'copy' || patch.op === 'move')) {
                    ensurePath(document, patch.from);
                }
            }
        });

        // For TRUE v2 compatibility, process patches ONE AT A TIME
        // v2 continues processing even when test operations fail (returns false)
        // v3 would throw immediately, so we need individual processing
        const results = [];

        for (let i = 0; i < patches.length; i++) {
            const patch = patches[i];

            try {
                // Apply single patch
                const v3Result = v3.apply(document, [patch], validate, true, true);

                // v2 behavior for successful operations:
                // - 'test' returns boolean (true if passed)
                // - 'remove' returns the removed value (v3 has it in removed array)
                // - 'replace' returns the old value
                // - 'move' returns the old value at destination
                // - 'add'/'copy' return undefined or null

                switch (patch.op) {
                    case 'test':
                        results.push(true); // test passed
                        break;
                    case 'remove':
                    case 'replace':
                        // v3 tracks removed values but not per-operation easily accessible
                        // v2 returned undefined/null for most cases
                        results.push(v3Result[0]?.removed);
                        break;
                    case 'move':
                        results.push(undefined);
                        break;
                    case 'add':
                    case 'copy':
                    default:
                        results.push(undefined);
                        break;
                }
            } catch (error) {
                // v2 CRITICAL BEHAVIOR: test failures don't throw!
                // They return false and continue processing
                if (patch.op === 'test' && error.name === 'TEST_OPERATION_FAILED') {
                    results.push(false);
                    continue; // Continue with next patch
                }

                // For other operations, check if error should be lenient
                if (isLenientError(error)) {
                    // silently continue processing (v2 compat)
                    // console.warn('[v2-compat] Lenient error handling:', error.message);
                    results.push(undefined);
                    continue;
                }

                // Re-throw critical errors
                error.message = `[v2-compat] ${error.message}`;
                throw error;
            }
        }

        return results;
    } catch (error) {
        // v2 compatibility: try to make errors less strict
        // Check if it's a validation error that v2 would ignore
        if (isLenientError(error)) {
            // Return empty results array (v2 lenient behavior)
            // silently continue processing (v2 compat)
            // console.warn('[v2-compat] Lenient error handling:', error.message);
            return [];
        }

        // Re-throw critical errors
        error.message = `[v2-compat] ${error.message}`;
        throw error;
    }
}

/**
 * Generate a JSON Patch sequence by comparing two documents (v2-compatible)
 *
 * @param {Object} from - The original document
 * @param {Object} to - The target document
 * @param {Object} options - Options object
 * @param {boolean} options.convertMongodbObjects - Whether to convert MongoDB ObjectIDs to strings (default: true)
 * @returns {Array} Array of JSON Patch operations
 *
 * @example
 * const patch = compare({ foo: 'bar' }, { foo: 'baz' });
 * // patch = [{ op: 'replace', path: '/foo', value: 'baz' }]
 */
function compare(from, to, options = {}) {
    try {
        // compare() has the same signature in v2 and v3
        return v3.compare(from, to, options);
    } catch (error) {
        // just in case
        error.message = `[v2-compat] ${error.message}`;
        throw error;
    }
}
/**
 * Validate a JSON Patch sequence (v2-compatible)
 *
 * Returns error object if invalid, undefined if valid (v2 format).
 * v3 returns null for valid, but v2 returns undefined.
 *
 * @param {Array} patches - Array of JSON Patch operations to validate
 * @param {Object} document - Optional document to validate against
 * @returns {Object|undefined} Error if invalid, undefined if valid
 *
 * @example
 * const error = validate([{ op: 'invalid', path: '/foo' }]);
 * // error = Error object with details
 */
function validate(patches, document) {
    try {
        const error = v3.validate(patches, document);

        // v3 returns null for valid, v2 returns undefined
        return error === null ? undefined : error;
    } catch (error) {
        // v2 compatibility: catch errors and return them instead of throwing
        return error;
    }
}

/**
 * Check if an error should be handled leniently
 *
 * v3 throws errors for paths it cannot resolve. Some of these
 * (like constructor access) should be caught and handled gracefully
 * by returning empty results, rather than throwing.
 *
 * This provides a softer failure mode for edge cases while still
 * preventing the operation from succeeding.
 *
 * @param {Error} error - The error to check
 * @returns {boolean} True if error should be handled leniently
 * @private
 */
function isLenientError(error) {
    if (!error || !error.message) return false;

    const message = error.message.toLowerCase();

    // Handle these v3 errors gracefully (return [] instead of throw)
    const lenientPatterns = [
        'operation path unresolvable',
        'operation from unresolvable',
        'path does not exist',
        'cannot perform'
    ];

    return lenientPatterns.some(pattern => message.includes(pattern));
}

/**
 * Deep clone utility (v2-compatible)
 *
 * @param {*} obj - The object to clone
 * @returns {*} Deep cloned copy
 */
function deepClone(obj) {
    // v2 used JSON.parse(JSON.stringify())
    // v3 has a more robust deepClone, but for v2 compat we use the old method
    if (obj === undefined || obj === null) return obj;
    if (typeof obj !== 'object') return obj;

    try {
        return JSON.parse(JSON.stringify(obj));
    } catch {
        // Fallback to v3's deepClone for complex objects
        return v3.deepClone(obj);
    }
}

// Export v2-compatible API
module.exports = {
    // Core operations (v2 public API)
    apply,
    compare,

    // Additional operations (bonus features)
    validate, // Was internal-only in v2, now public
    validator: v3.validator, // Single operation validator (internal in v2)
    deepClone, // New utility

    // Observer pattern (from v3, was internal-only in v2)
    observe: v3.observe,
    unobserve: v3.unobserve,
    generate: v3.generate, // Generate patches from observer

    // Path utilities
    escapePathComponent: v3.escapePathComponent,
    unescapePathComponent, // Our local implementation

    // Internal utilities (prefixed with _)
    _isLenientError: isLenientError,
    _v3: v3 // Access to underlying v3 if needed
};
